import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CheckCircle, Lightning } from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Subscribe() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [lib, setLib] = useState(null);

  useEffect(() => {
    api.get("/paypal/config").then((r) => setCfg(r.data)).catch(() => {});
    if (user) api.get("/library").then((r) => setLib(r.data)).catch(() => {});
  }, [user]);

  const cancel = async () => {
    try {
      await api.post("/paypal/subscriptions/cancel");
      toast.success("Subscription cancelled");
      const r = await api.get("/library");
      setLib(r.data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const perks = [
    "Unlimited access to every workout class",
    "New classes added continuously",
    "Cancel anytime — no lock-in",
    "Works on any device",
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="subscribe-page">
      <span className="label-overline">// All-Access Pass</span>
      <h1 className="font-display text-5xl sm:text-6xl tracking-widest leading-none mt-3 mb-3">
        Every class.<br />One price.
      </h1>
      <p className="text-zinc-400 max-w-lg">Go all-in. Unlimited streaming, every new drop, zero friction.</p>

      <div className="mt-12 border border-white/10 p-10 grain relative" data-testid="subscribe-card">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-7xl text-[#D4A574]" data-testid="sub-price">£{cfg?.subscription_price || "14.99"}</span>
          <span className="label-overline">/ month</span>
        </div>
        <ul className="mt-8 space-y-3">
          {perks.map((p) => (
            <li key={p} className="flex items-center gap-3 text-zinc-200">
              <CheckCircle weight="duotone" size={22} className="text-[#D4A574] flex-shrink-0" /> {p}
            </li>
          ))}
        </ul>

        <div className="mt-10">
          {!user ? (
            <button onClick={() => nav("/login")} className="kinetic-btn" data-testid="sub-login-btn">Login to Subscribe</button>
          ) : lib?.has_subscription ? (
            <div data-testid="active-subscription">
              <div className="flex items-center gap-2 text-[#D4A574] font-display text-2xl tracking-wider">
                <Lightning weight="fill" /> Subscription Active
              </div>
              <p className="text-sm text-zinc-400 mt-2">Status: {lib.subscription?.status}</p>
              <button onClick={cancel} className="label-overline border border-white/20 px-5 py-3 mt-5 hover:border-[#FF3B30] hover:text-[#FF3B30]" data-testid="cancel-sub-btn">
                Cancel Subscription
              </button>
            </div>
          ) : cfg?.client_id && cfg?.plan_id ? (
            <div data-testid="paypal-sub-wrapper">
              <PayPalScriptProvider options={{ clientId: cfg.client_id, vault: true, intent: "subscription" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "black", shape: "rect", label: "subscribe" }}
                  createSubscription={(data, actions) => actions.subscription.create({ plan_id: cfg.plan_id })}
                  onApprove={async (data) => {
                    try {
                      await api.post("/paypal/subscriptions/activate", { subscription_id: data.subscriptionID });
                      toast.success("Subscription active. All classes unlocked.");
                      const r = await api.get("/library");
                      setLib(r.data);
                    } catch (e) { toast.error(formatErr(e)); }
                  }}
                  onError={(err) => { console.error(err); toast.error("PayPal error"); }}
                />
              </PayPalScriptProvider>
            </div>
          ) : (
            <p className="text-sm text-zinc-500">PayPal not configured.</p>
          )}
        </div>
      </div>
    </div>
  );
}
